/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect } from "react";
import "./App.scss";
import { LiveAPIProvider, useLiveAPIContext } from "./contexts/LiveAPIContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
// import SidePanel from "./components/side-panel/SidePanel";
import { Altair } from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import Login from "./components/login/Login";
import Header from "./components/header/Header";
// import Aurora from "./components/aurora/Aurora";
import ConversationHistory from "./components/conversation-history/ConversationHistory";
import SupabaseDebugger from "./components/debug/SupabaseDebugger";
import { SafariWarning } from "./components/safari-warning/SafariWarning";
import cn from "classnames";
import * as analytics from "./lib/analytics";
import { detectBrowser } from "./lib/utils";
import ThreeVisualization from "./components/visualizer/ThreeVisualization";
// import OrbitViz from "./components/visualizer/OrbitViz";

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
if (typeof API_KEY !== "string") {
  throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
}

const host = "generativelanguage.googleapis.com";
const uri = `wss://${host}/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent`;

// Chat content component that has access to LiveAPI context
const ChatContentWithContext: React.FC<{
  currentView: 'chat' | 'history' | 'debug';
  videoRef: React.RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
  setVideoStream: (stream: MediaStream | null) => void;
}> = ({ currentView, videoRef, videoStream, setVideoStream }) => {
  const { connected, volume } = useLiveAPIContext();
  const [webcamStreaming, setWebcamStreaming] = useState(false);
  const [screenStreaming, setScreenStreaming] = useState(false);

  return (
    <>
      {currentView === 'chat' ? (
        <div className="streaming-console">
          <main>
            <div className="main-app-area">
              {/* APP goes here */}
              <div style={{ width: '100%', height: '100%', position: 'relative', minHeight: '500px' }}>
                <ThreeVisualization
                  listening={connected}
                  volume={volume}
                  hideVisualization={webcamStreaming || screenStreaming}
                />
                {/* <OrbitViz listening={connected} volume={volume} /> */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 5,
                  pointerEvents: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '80%',
                  height: '80%',
                  maxWidth: '800px',
                  maxHeight: '600px'
                }}>
                  <Altair />
                </div>
                <video
                  className={cn("stream", {
                    hidden: !videoRef.current || !videoStream,
                  })}
                  style={{
                    position: webcamStreaming || screenStreaming ? 'absolute' : 'relative',
                    top: webcamStreaming || screenStreaming ? '50%' : 'auto',
                    left: webcamStreaming || screenStreaming ? '50%' : 'auto',
                    transform: webcamStreaming || screenStreaming ? 'translate(-50%, -50%)' : 'none',
                    zIndex: webcamStreaming || screenStreaming ? 10 : 'auto',
                    maxWidth: webcamStreaming || screenStreaming ? '80%' : '100%',
                    maxHeight: webcamStreaming || screenStreaming ? '80%' : '100%',
                  }}
                  ref={videoRef}
                  autoPlay
                  playsInline
                />
              </div>
            </div>

            <ControlTray
              videoRef={videoRef}
              supportsVideo={true}
              onVideoStreamChange={setVideoStream}
              onWebcamStreamingChange={setWebcamStreaming}
              onScreenStreamingChange={setScreenStreaming}
            >
              {/* put your own buttons here */}
            </ControlTray>
          </main>
        </div>
      ) : currentView === 'history' ? (
        <div className="history-view">
          <ConversationHistory />
        </div>
      ) : (
        <div className="debug-view">
          <SupabaseDebugger />
        </div>
      )}
    </>
  );
};

// Main application content component
const AppContent = () => {
  const { user, signOut } = useAuth();
  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  // feel free to style as you see fit
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [currentView, setCurrentView] = useState<'chat' | 'history' | 'debug'>('chat');
  
  // Track page views when view changes
  useEffect(() => {
    // Track the view change
    analytics.pageview(`/${currentView}`, `JAR-ViS - ${currentView.charAt(0).toUpperCase() + currentView.slice(1)}`);
  }, [currentView]);

  // Track user login/logout
  useEffect(() => {
    if (user) {
      analytics.trackLogin('supabase');
    }
  }, [user]);
  
  const handleLogout = () => {
    analytics.event('logout');
    signOut();
  };

  const toggleView = (view: 'chat' | 'history' | 'debug') => {
    setCurrentView(view);
  };

  return (
    <>
      {!user ? (
        <Login onLogin={() => {}} />
      ) : (
        <LiveAPIProvider url={uri} apiKey={API_KEY}>
          <Header 
            onLogout={handleLogout} 
            onToggleHistory={() => toggleView(currentView === 'chat' ? 'history' : 'chat')} 
            onToggleDebug={() => toggleView(currentView === 'debug' ? 'chat' : 'debug')}
            currentView={currentView} 
          />
          <ChatContentWithContext 
            currentView={currentView}
            videoRef={videoRef}
            videoStream={videoStream}
            setVideoStream={setVideoStream}
          />
        </LiveAPIProvider>
      )}
    </>
  );
};

function App() {
  const [browserInfo, setBrowserInfo] = useState<{ isSafari: boolean; isFirefox: boolean; isChrome: boolean; isChromeOnIOS: boolean } | null>(null);
  
  useEffect(() => {
    analytics.initialize();
    
    // Detect browser on mount
    const browser = detectBrowser();
    setBrowserInfo(browser);
  }, []);

  // Show warning if unsupported browser is detected (Safari or Firefox)
  // Allow Chrome on iOS even though it uses Safari's WebKit engine
  if ((browserInfo?.isSafari && !browserInfo?.isChromeOnIOS) || browserInfo?.isFirefox) {
    return <SafariWarning />;
  }

  return (
    <div className="App">
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </div>
  );
}

export default App;
